import React, { useState, memo } from "react";
import { useTranslation } from "react-i18next";
import { useShare } from "../../hooks/useShare";
import { usePromoCountdown } from "../../hooks/usePromoCountdown";
import { formatPrice } from "../../utils/formatPrice";
import { getOptimizedImageUrl, getImageSrcSet } from "../../utils/imageUrl";
import "./ProductCard.css";

function ProductCard({
  item,
  viewMode = "grid",
  onAddToCart,
  onItemClick,
  getTranslatedText,
  isFavorite = false,
  onToggleFavorite,
  index = 0,
  restaurantId,
  tableNumber,
  mode = "dine_in",
  onPromoExpired,
}) {
  const { t } = useTranslation();
  const { shareProduct } = useShare();
  const [shareToast, setShareToast] = useState(null);
  const [addAnimating, setAddAnimating] = useState(false);

  const isFlashPromo = item.promoType === 'flash' && item.promoEndsAt;
  const { timeLeft, isExpired } = usePromoCountdown(
    isFlashPromo ? item.promoEndsAt : null,
    onPromoExpired
  );
  const hasPromoPrice = item.promoPrice != null && item.promoPrice < item.price;

  const name = getTranslatedText(item, "name");
  const description = getTranslatedText(item, "description");

  const handleClick = (e) => {
    // If clicking on action buttons, don't trigger item click
    if (
      e.target.closest(".product-card__add-btn") ||
      e.target.closest(".product-card__favorite-btn") ||
      e.target.closest(".product-card__share-btn")
    ) {
      return;
    }
    if (onItemClick) {
      onItemClick(item);
    }
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite(item.itemId);
    }
  };

  const handleShareClick = async (e) => {
    e.stopPropagation();
    const result = await shareProduct(item, restaurantId, tableNumber, mode);
    if (result.success) {
      const message =
        result.method === "clipboard" ? t("share.linkCopied") : t("share.shared");
      setShareToast(message);
      setTimeout(() => setShareToast(null), 2000);
    } else if (!result.cancelled) {
      setShareToast(t("share.error"));
      setTimeout(() => setShareToast(null), 2000);
    }
  };

  const handleAddClick = (e) => {
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(item);
      setAddAnimating(true);
      setTimeout(() => setAddAnimating(false), 400);
    }
  };

  return (
    <article
      className={`product-card product-card--${viewMode}`}
      style={{
        "--card-index": Math.min(index, 15),
        ...(item.isPromotion && item.highlightColor
          ? { borderColor: item.highlightColor, borderWidth: "2px" }
          : {}),
      }}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleClick(e);
        }
      }}
    >
      <div className="product-card__image-container">
        {item.imageUrl ? (
          <img
            src={getOptimizedImageUrl(item.imageUrl, {
              width: viewMode === "list" ? 80 : 300,
            })}
            srcSet={getImageSrcSet(
              item.imageUrl,
              viewMode === "list" ? 80 : 300,
            )}
            sizes={
              viewMode === "list" ? "80px" : "(max-width: 480px) 45vw, 300px"
            }
            alt={name}
            loading="lazy"
            decoding="async"
            className="product-card__image"
          />
        ) : (
          <div className="product-card__image-placeholder">
            <span className="material-icons" aria-hidden="true">restaurant</span>
          </div>
        )}

        {/* Action buttons - inside image for grid/cards */}
        {viewMode !== "list" && (
          <div className="product-card__actions">
            <button
              className={`product-card__favorite-btn ${isFavorite ? "active" : ""}`}
              onClick={handleFavoriteClick}
              aria-label={isFavorite ? t("favorites.remove") : t("favorites.add")}
            >
              <span className="material-icons" aria-hidden="true">
                {isFavorite ? "favorite" : "favorite_border"}
              </span>
            </button>
            <button
              className="product-card__share-btn"
              onClick={handleShareClick}
              aria-label={t("share.share")}
            >
              <span className="material-icons" aria-hidden="true">share</span>
            </button>
          </div>
        )}

        {/* Share toast */}
        {shareToast && (
          <div className="product-card__toast" role="status" aria-live="polite">
            {shareToast}
          </div>
        )}

        {/* Badges for combos, specials, popularity and discounts */}
        <div className="product-card__badges">
          {item.popularityBadge === 'top_today' && (
            <span className="product-card__badge product-card__badge--top">
              <span className="material-icons product-card__badge-icon" aria-hidden="true">star</span>
              {t('badges.topToday')}
            </span>
          )}
          {item.popularityBadge === 'trending' && (
            <span className="product-card__badge product-card__badge--trending">
              <span className="material-icons product-card__badge-icon" aria-hidden="true">trending_up</span>
              {t('badges.trending')}
            </span>
          )}
          {item.isCombo && (
            <span className="product-card__badge product-card__badge--combo">
              {t('promos.combos')}
            </span>
          )}
          {item.isDailySpecial && (
            <span className="product-card__badge product-card__badge--special">
              {t('stories.dailySpecials')}
            </span>
          )}
          {item.isPromotion && item.promotionBadge && (
            <span className="product-card__badge product-card__badge--promo">
              {item.promotionBadge}
            </span>
          )}
          {item.promoType && !item.promotionBadge && (
            <span className="product-card__badge product-card__badge--promo">
              {t(`promos.types.${item.promoType}`)}
            </span>
          )}
          {hasPromoPrice && (
            <span
              className="product-card__badge product-card__badge--discount"
              title={t("menu.discountBadge", { percent: Math.round((1 - item.promoPrice / item.price) * 100) })}
              aria-label={t("menu.discountBadge", { percent: Math.round((1 - item.promoPrice / item.price) * 100) })}
            >
              -{Math.round((1 - item.promoPrice / item.price) * 100)}%
            </span>
          )}
          {!hasPromoPrice && item.originalPrice && item.originalPrice > item.price && (
            <span
              className="product-card__badge product-card__badge--discount"
              title={t("menu.discountBadge", { percent: Math.round((1 - item.price / item.originalPrice) * 100) })}
              aria-label={t("menu.discountBadge", { percent: Math.round((1 - item.price / item.originalPrice) * 100) })}
            >
              -{Math.round((1 - item.price / item.originalPrice) * 100)}%
            </span>
          )}
        </div>
      </div>

      <div className="product-card__content">
        <h3 className="product-card__name">{name}</h3>
        {description && (
          <p className="product-card__description">{description}</p>
        )}

        {item.allergens && item.allergens.length > 0 && (
          <div
            className="product-card__allergens"
            aria-label={t("menu.allergens")}
          >
            <span className="material-icons product-card__allergens-icon" aria-hidden="true">
              warning_amber
            </span>
            <span className="product-card__allergens-list">
              {item.allergens.join(", ")}
            </span>
          </div>
        )}

        <div className="product-card__footer">
          <div className="product-card__price-container">
            {hasPromoPrice && !isExpired ? (
              <>
                <span className="product-card__original-price">
                  ${formatPrice(item.price)}
                </span>
                <span className="product-card__price product-card__price--promo">
                  ${formatPrice(item.promoPrice)}
                </span>
              </>
            ) : (
              <>
                {item.originalPrice && item.originalPrice > item.price && (
                  <span className="product-card__original-price">
                    ${formatPrice(item.originalPrice)}
                  </span>
                )}
                <span className="product-card__price">
                  ${formatPrice(item.price)}
                </span>
              </>
            )}
          </div>
          {isFlashPromo && !isExpired && timeLeft && (
            <div className="product-card__countdown" aria-live="polite" aria-label={t('promos.endsIn', { time: timeLeft })}>
              <span className="material-icons product-card__countdown-icon" aria-hidden="true">timer</span>
              <span className="product-card__countdown-time">{timeLeft}</span>
            </div>
          )}
          {/* Action buttons - inline in footer for list view */}
          {viewMode === "list" && (
            <div className="product-card__actions product-card__actions--inline">
              <button
                className={`product-card__favorite-btn ${isFavorite ? "active" : ""}`}
                onClick={handleFavoriteClick}
                aria-label={isFavorite ? t("favorites.remove") : t("favorites.add")}
              >
                <span className="material-icons" aria-hidden="true">
                  {isFavorite ? "favorite" : "favorite_border"}
                </span>
              </button>
              <button
                className="product-card__share-btn"
                onClick={handleShareClick}
                aria-label={t("share.share")}
              >
                <span className="material-icons" aria-hidden="true">share</span>
              </button>
            </div>
          )}
          <button
            className={`product-card__add-btn${addAnimating ? " product-card__add-btn--added" : ""}`}
            onClick={handleAddClick}
            aria-label={t("menu.addToCart")}
          >
            <span className="material-icons" aria-hidden="true">add</span>
          </button>
        </div>
      </div>
    </article>
  );
}

export default memo(ProductCard);
